import type { SxProps, Theme } from "@mui/material/styles";
import { type FC } from "react";
import { resolveCdnAssetUrl } from "../utils/cdnAssetUrl";
import { CdnImage } from "./common/CdnImage";

interface ImageFromFileNameProps {
  hasPath?: boolean;
  fileName: string;
  fileType?: "png" | "jpg" | "jpeg" | "svg" | "webp";
  sx?: SxProps<Theme>;
  alt?: string;
}

const defaultSx: SxProps<Theme> = [];

const normalizeAssetFileName = (
  fileName: string,
  fallbackExt: string,
): string => {
  const trimmed = fileName.replace(/^\//, "");
  const lastSegment = trimmed.split("/").pop() ?? trimmed;
  const hasExt = lastSegment.includes(".");
  return hasExt ? trimmed : `${trimmed}.${fallbackExt}`;
};

const toDefaultAltText = (fileName: string): string => {
  const lastSegment = fileName.replace(/^\//, "").split("/").pop() ?? fileName;
  const withoutExtension = lastSegment.replace(/\.[^.]+$/, "");
  const readable = withoutExtension.replace(/[-_]+/g, " ").trim();
  return readable ? `Image: ${readable}` : "Image";
};

const isAbsoluteUrl = (source: string): boolean => /^https?:\/\//i.test(source);

const toCdnOrAbsoluteImageUrl = (fileName: string, fallbackExt: string): string => {
  if (isAbsoluteUrl(fileName)) {
    return fileName;
  }

  return resolveCdnAssetUrl(normalizeAssetFileName(fileName, fallbackExt));
};

export const ImageFromFileName: FC<ImageFromFileNameProps> = ({
  fileName,
  fileType = "png",
  sx = defaultSx,
  alt,
}) => {
  if (!fileName) {
    return null;
  }
  const resolvedSrc = toCdnOrAbsoluteImageUrl(fileName, fileType);
  const resolvedAlt = alt ?? toDefaultAltText(fileName);

  return (
    <CdnImage
      alt={resolvedAlt}
      src={resolvedSrc}
      sx={sx}
    />
  );
};
